// グラフの共通テーマ（トラッカー G1/G2。設計: docs/plans/2026-09-27-ui-visual-redesign-spec.md）
//
// cmp/utils/chartTheme.js と cli/utils/chartTheme.js は同じ内容にする（テストで一致を検証）。
// chart.js 2.9 / vue-chartjs 3.5 前提。chart.js 本体は import せず、プラグイン
// （plugins/chartTheme.js）から Chart を渡して既定値を設定する。
//
// - 落ち着いた8色のパレット。原色（ネオン系）は使わない
// - 線はなめらか（tension 0.3）・線幅2・点は最終点のみ
// - 罫線は横線のみ・薄く
// - ツールチップに日付と単位
// - 体組成グラフ（BodyMetricChart）は1指標1枚。選べる指標は本部設定 lesson_attrs_setting の use:true だけ
import { dayjsTz as dayjs } from '~/utils/dayjs'

export const CHART_PALETTE = [
  '#2F54EB', // 青（CMP の primary）
  '#13A8A8', // 青緑
  '#E8833A', // 橙
  '#8B6BD6', // 紫
  '#D4A017', // 黄土
  '#D1568A', // 薔薇
  '#4F9D69', // 緑
  '#6B7A99' // 灰青
]

export const CHART_FONT_FAMILY =
  "'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Noto Sans JP', Meiryo, sans-serif"
export const CHART_TEXT_COLOR = '#7A8099' // 補助文字（トークン）
export const CHART_GRID_COLOR = '#EEF0F5' // 罫線より一段薄く
export const CHART_TOOLTIP_BG = '#0A1233' // 本文色（トークン）
export const CHART_LINE_TENSION = 0.3
export const CHART_LINE_WIDTH = 2

// 本部設定 lesson_attrs_setting の計測項目（数値で記録できるもの）。並びは設定画面と同じ
export const BODY_METRIC_KEYS = [
  // 体組成
  'weight',
  'fat_rate',
  'basal_metabolism',
  'bmi',
  'muscle_weight',
  'fat_weight',
  'water_weight',
  // サイズ
  'neck_girth_length',
  'upper_arm_girth_length',
  'bust_girth_length',
  'waist_girth_length',
  'hip_girth_length',
  'thigh_girth_length',
  // 重量（BIG3）
  'max_weight_bench_press',
  'max_weight_squat',
  'max_weight_deadlift',
  // カスタム数値
  'custom_number_1',
  'custom_number_2',
  'custom_number_3',
  'custom_number_4',
  'custom_number_5'
]

// 既定で出す指標（ユーザー決定 2026-09-27）。新規本部の chart 既定値と同じ。
// 2026-09-28 以降、chart:true が0件のときは「利用中の先頭2項目」を出す（defaultMetricKeys）
export const DEFAULT_METRIC_KEYS = ['weight', 'fat_rate']

// 期間チップ（ユーザー決定 2026-09-27）
export const PERIOD_OPTIONS = [
  { key: '1m', label: '1か月', months: 1 },
  { key: '3m', label: '3か月', months: 3 },
  { key: '6m', label: '6か月', months: 6 },
  { key: '1y', label: '1年', months: 12 },
  { key: 'custom', label: 'カスタム' }
]
export const DEFAULT_PERIOD_KEY = '1y'
export const MAX_CUSTOM_PERIOD_YEARS = 10

const DATE_FORMAT = 'YYYY-MM-DD'

// 期間チップ → { from, to }（YYYY-MM-DD）。custom は null（日付は利用者が入れる）
export function periodRange(key, now) {
  const option = PERIOD_OPTIONS.find(o => o.key === key)
  if (!option || !option.months) return null
  const to = dayjs(now)
  return {
    from: to.subtract(option.months, 'month').format(DATE_FORMAT),
    to: to.format(DATE_FORMAT)
  }
}

// カスタム期間の検証。問題なければ null、あればメッセージ
export function validateCustomRange(from, to) {
  if (!from || !to) return '開始日と終了日を入力してください'
  const f = dayjs(from)
  const t = dayjs(to)
  if (!f.isValid() || !t.isValid()) return '日付の形式が正しくありません'
  if (t.isBefore(f)) return '終了日は開始日以降にしてください'
  if (f.add(MAX_CUSTOM_PERIOD_YEARS, 'year').isBefore(t)) {
    return `期間は${MAX_CUSTOM_PERIOD_YEARS}年以内にしてください`
  }
  return null
}

// 本部設定から選べる指標の一覧（use:true だけ）。[{ key, title, unit, chart }]
// chart: 本部設定の「グラフ初期表示」（lesson_attrs_setting.*.chart）。最初に表示する指標（2026-09-28）
export function availableMetrics(lessonSetting) {
  if (!lessonSetting) return []
  return BODY_METRIC_KEYS.filter(key => {
    const s = lessonSetting[key]
    return s && s.use
  }).map(key => {
    const s = lessonSetting[key]
    const n = key.match(/^custom_number_(\d)$/)
    const fallback = n ? `カスタム${n[1]}` : key
    return {
      key,
      title: s.title || fallback,
      unit: s.tani || '',
      chart: !!s.chart
    }
  })
}

const hasValue = v => v !== null && v !== undefined && v !== '' && !isNaN(Number(v))

// 期間内に記録がある指標のキー
export function metricKeysWithData(trends) {
  const keys = new Set()
  ;(trends || []).forEach(tr => {
    BODY_METRIC_KEYS.forEach(key => {
      if (hasValue(tr[key])) keys.add(key)
    })
  })
  return keys
}

// 既定の表示指標（端末に記憶した選択が無いとき）:
// 1. 本部設定で「グラフ初期表示」（chart:true）かつ利用中（use:true）の項目（設定画面の並び。2026-09-28 ユーザー決定）
// 2. 1が0件なら利用中の項目の先頭2つ（既定の本部では体重・体脂肪率。体重・体脂肪率を使わない店舗もあるため 2026-09-28 変更）
// trends は以前の「データのある項目」判定の名残（呼び出し側の互換のため引数は残す）
// eslint-disable-next-line no-unused-vars
export function defaultMetricKeys(available, trends) {
  const charted = available.filter(m => m.chart).map(m => m.key)
  if (charted.length > 0) return charted
  return available.map(m => m.key).slice(0, 2)
}

// 端末に記憶した選択を、今選べる指標で絞る。記憶が無ければ既定
export function resolveSelectedKeys(stored, available, trends) {
  if (Array.isArray(stored)) {
    const availableKeys = available.map(m => m.key)
    return stored.filter(k => availableKeys.includes(k))
  }
  return defaultMetricKeys(available, trends)
}

// 端末への記憶（CMP=スタッフの端末、会員アプリ=会員の端末）。本部ごとに分ける
// localStorage はプライベートウィンドウ等で throw するので握りつぶす
export const selectedMetricsStorageKey = headquarterId =>
  `gyms.bodyMetricChart.selected.${headquarterId || 'default'}`

export function loadSelectedKeys(headquarterId, storage) {
  try {
    const s = storage || window.localStorage
    const raw = s.getItem(selectedMetricsStorageKey(headquarterId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch (e) {
    return null
  }
}

export function saveSelectedKeys(headquarterId, keys, storage) {
  try {
    const s = storage || window.localStorage
    s.setItem(selectedMetricsStorageKey(headquarterId), JSON.stringify(keys))
  } catch (e) {
    // 記憶できなくても表示は続ける
  }
}

// 1指標の点列（日付昇順・値のある日だけ）。[{ date, value }]
export function metricSeries(trends, key) {
  return (trends || [])
    .filter(tr => hasValue(tr[key]))
    .map(tr => ({ date: tr.date, value: Number(tr[key]) }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

// 最新値と前回との差
export function summarizeSeries(series) {
  const n = series.length
  if (n === 0) return { latest: null, latestDate: null, diff: null }
  const last = series[n - 1]
  const diff = n >= 2 ? roundTo(last.value - series[n - 2].value, 1) : null
  return { latest: last.value, latestDate: last.date, diff }
}

function roundTo(v, digits) {
  const p = Math.pow(10, digits)
  return Math.round(v * p) / p
}

// 数値の表示（小数1桁まで・3桁区切り）
export function formatMetricValue(value) {
  if (!hasValue(value)) return '-'
  const rounded = roundTo(Number(value), 1)
  const [int, dec] = String(Math.abs(rounded)).split('.')
  const withComma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${rounded < 0 ? '-' : ''}${withComma}${dec ? '.' + dec : ''}`
}

// 前回との差の表示（+0.4kg / -0.3kg / ±0kg）
export function formatDiff(diff, unit) {
  if (diff === null || diff === undefined) return ''
  const u = unit || ''
  if (diff === 0) return `±0${u}`
  return `${diff > 0 ? '+' : '-'}${formatMetricValue(Math.abs(diff))}${u}`
}

// 日付の表示（2026/9/27）。YYYY-MM-DD 以外はそのまま返す
export function formatChartDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return value
  return dayjs(value.slice(0, 10)).format('YYYY/M/D')
}

// 目標値。目標期間（チャレンジ）の目標を優先し、無ければ会員の目標
export function resolveGoal(key, client, goalTermGoals) {
  const candidates = [
    goalTermGoals ? goalTermGoals[key] : null,
    client ? client[`goal_term_goal_${key}`] : null,
    client ? client[`goal_${key}`] : null
  ]
  const found = candidates.find(hasValue)
  return found === undefined ? null : Number(found)
}

// 16進色 → rgba
export function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return `rgba(47, 84, 235, ${alpha})`
  const n = parseInt(full, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

export const paletteColor = index => CHART_PALETTE[index % CHART_PALETTE.length]

// 折れ線1本の見た目。点は最終点のみ
export function lineDatasetStyle(color, length) {
  const radius = Array.from({ length: length || 0 }, (_, i) => (i === length - 1 ? 3 : 0))
  return {
    type: 'line',
    borderColor: color,
    backgroundColor: hexToRgba(color, 0.08),
    pointBackgroundColor: color,
    pointBorderColor: color,
    borderWidth: CHART_LINE_WIDTH,
    lineTension: CHART_LINE_TENSION,
    pointRadius: radius,
    pointHoverRadius: 4,
    pointHitRadius: 8,
    fill: 'start',
    spanGaps: true
  }
}

// 目標線（chartjs-plugin-annotation 0.5）。細い点線・控えめな色
export function goalAnnotation(value, scaleID, label) {
  return {
    type: 'line',
    drawTime: 'afterDatasetsDraw',
    mode: 'horizontal',
    scaleID,
    value,
    borderColor: CHART_TEXT_COLOR,
    borderWidth: 1,
    borderDash: [4, 4],
    label: {
      enabled: true,
      content: label,
      position: 'left',
      backgroundColor: 'rgba(255,255,255,0.85)',
      fontColor: CHART_TEXT_COLOR,
      fontSize: 10,
      fontStyle: 'normal',
      xPadding: 4,
      yPadding: 2,
      cornerRadius: 3,
      yAdjust: -8
    }
  }
}

// ツールチップのタイトル（ラベルが日付なら 2026/9/27 に）
function tooltipTitle(items, data) {
  if (!items || items.length === 0) return ''
  const item = items[0]
  const label =
    item.label !== undefined && item.label !== ''
      ? item.label
      : data && data.labels
        ? data.labels[item.index]
        : ''
  return formatChartDate(label)
}

const ensure = (obj, key) => {
  if (!obj[key] || typeof obj[key] !== 'object') obj[key] = {}
  return obj[key]
}

// Chart.defaults をテーマに合わせる（全グラフの底上げ）。各グラフの個別指定が優先される
export function applyChartDefaults(Chart) {
  if (!Chart || !Chart.defaults) return
  const defaults = Chart.defaults
  const global = ensure(defaults, 'global')

  global.defaultFontFamily = CHART_FONT_FAMILY
  global.defaultFontColor = CHART_TEXT_COLOR
  global.defaultFontSize = 11

  const legend = ensure(global, 'legend')
  const legendLabels = ensure(legend, 'labels')
  legendLabels.fontSize = 12
  legendLabels.boxWidth = 8
  legendLabels.padding = 16
  legendLabels.usePointStyle = true

  const tooltips = ensure(global, 'tooltips')
  tooltips.mode = 'index'
  tooltips.intersect = false
  tooltips.backgroundColor = CHART_TOOLTIP_BG
  tooltips.titleFontSize = 12
  tooltips.titleFontStyle = 'bold'
  tooltips.bodyFontSize = 12
  tooltips.xPadding = 10
  tooltips.yPadding = 8
  tooltips.cornerRadius = 6
  tooltips.caretSize = 5
  tooltips.displayColors = true
  tooltips.boxWidth = 8
  tooltips.boxHeight = 8
  const callbacks = ensure(tooltips, 'callbacks')
  callbacks.title = tooltipTitle

  const hover = ensure(global, 'hover')
  hover.mode = 'index'
  hover.intersect = false

  const elements = ensure(global, 'elements')
  const line = ensure(elements, 'line')
  line.tension = CHART_LINE_TENSION
  line.borderWidth = CHART_LINE_WIDTH
  const point = ensure(elements, 'point')
  point.radius = 2
  point.hoverRadius = 4
  point.hitRadius = 8
  const rectangle = ensure(elements, 'rectangle')
  rectangle.borderWidth = 0

  // 罫線: 横線のみ・薄く（縦線は折れ線・棒の X 軸で消す）
  const scale = ensure(defaults, 'scale')
  const gridLines = ensure(scale, 'gridLines')
  gridLines.color = CHART_GRID_COLOR
  gridLines.zeroLineColor = CHART_GRID_COLOR
  gridLines.drawBorder = false
  gridLines.tickMarkLength = 6
  const ticks = ensure(scale, 'ticks')
  ticks.padding = 6
  ;['line', 'bar'].forEach(type => {
    const typeDefaults = defaults[type]
    if (!typeDefaults || !typeDefaults.scales || !Array.isArray(typeDefaults.scales.xAxes)) return
    const x = typeDefaults.scales.xAxes[0]
    if (!x) return
    x.gridLines = Object.assign({}, x.gridLines, { display: false })
  })
}
